import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Button, cn, useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { isSameDay } from "date-fns/isSameDay";
import { isSameMonth } from "date-fns/isSameMonth";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export interface CompactMonthDayButtonProps {
  date: Date;
  events: TellescopeModelFromName<"calendar_events">[];
  onClick: (() => void) | { to: string; replace?: boolean };
  referenceDate: Date;
}

export const CompactMonthDayButton = ({ date, events, onClick, referenceDate }: CompactMonthDayButtonProps) => {
  const { t } = useTranslation("calendar");
  const { format } = useDateFnsFormattersWithLocale();
  const today = new Date();

  const eventsToDisplay = useMemo(() => {
    if (events.length > 3) {
      return events.slice(0, 1);
    }

    return events;
  }, [events]);

  if (!isSameMonth(referenceDate, date)) {
    return <div className="w-full h-[44px]" />;
  }

  if (typeof onClick === "function") {
    return (
      <Button
        className="flex flex-col items-center justify-start w-full font-light h-[44px] p-0 pt-1 gap-0"
        variant="ghost"
      >
        <span
          className={cn(
            "rounded-full aspect-square flex items-center justify-center text-center text-xs",
            isSameDay(date, today) && "bg-primary text-primary-foreground"
          )}
        >
          {format(date, "d")}
        </span>
        <span className="flex gap-0.5">
          {eventsToDisplay.map((event) => (
            <span
              key={event.id}
              className="rounded-full size-1.5 bg-gray-500"
              style={{ backgroundColor: event.color }}
            />
          ))}
          {eventsToDisplay.length !== events.length && (
            <span className="text-[7px] text-muted-foreground leading-none mt-0.5">
              {t("+{{amount}}", { amount: events.length - eventsToDisplay.length })}
            </span>
          )}
        </span>
      </Button>
    );
  }

  return (
    <Button
      className="flex flex-col items-center justify-start w-full font-light h-[44px] p-0 pt-1 gap-0.5"
      variant="ghost"
      asChild
    >
      <Link to={onClick.to} replace={onClick.replace}>
        <span
          className={cn(
            "rounded-full aspect-square flex items-center justify-center text-center text-xs p-1",
            isSameDay(date, today) && "bg-primary text-primary-foreground"
          )}
        >
          {format(date, "d")}
        </span>
        <span className="flex gap-0.5">
          {eventsToDisplay.map((event) => (
            <span
              key={event.id}
              className="rounded-full size-1.5 bg-gray-500"
              style={{ backgroundColor: event.color }}
            />
          ))}
          {eventsToDisplay.length !== events.length && (
            <span className="text-[7px] text-muted-foreground leading-none mt-0.5">
              {t("+{{amount}}", { amount: events.length - eventsToDisplay.length })}
            </span>
          )}
        </span>
      </Link>
    </Button>
  );
};
